import React, { useEffect, useState } from "react";
import axios from "axios";
import { useAuth } from "../context/AuthContext";
import { useLang } from "../context/LangContext";
import { useBranding } from "../context/BrandingContext";
import Layout from "../components/Layout";
import { User, MessageSquare, Tag, Users, Plus, Trash2, Save, X, ShieldOff, ShieldCheck, Copy, Check, Link, Edit2, Palette } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const FRONTEND_URL = window.location.origin;

// UTC suffix'i olmayan MySQL tarihlerini doğru parse et
function parseUTC(dateStr) {
  if (!dateStr) return new Date();
  if (!dateStr.endsWith('Z') && !/[+\-]\d{2}:\d{2}$/.test(dateStr)) {
    return new Date(dateStr + 'Z');
  }
  return new Date(dateStr);
}

const TABS = [
  { key: "profile", icon: User, label_key: "settings.profile" },
  { key: "canned", icon: MessageSquare, label_key: "settings.canned" },
  { key: "tags", icon: Tag, label_key: "settings.tags" },
  { key: "team", icon: Users, label_key: "settings.team" },
  { key: "bans", icon: ShieldOff, label_key: "settings.bans" },
  { key: "branding", icon: Palette, label_key: "settings.branding" },
];

export default function Settings() {
  const { user } = useAuth();
  const { t } = useLang();
  const globalBranding = useBranding();
  const [tab, setTab] = useState("profile");
  const [sites, setSites] = useState([]);
  const [selectedSite, setSelectedSite] = useState(null);
  const [cannedResponses, setCannedResponses] = useState([]);
  const [tags, setTags] = useState([]);
  const [teamUsers, setTeamUsers] = useState([]);
  const [profile, setProfile] = useState({ name: user?.name || "", email: user?.email || "", password: "" });
  const [saving, setSaving] = useState(false);
  const [newCanned, setNewCanned] = useState({ title: "", content: "", shortcut: "" });
  const [newTag, setNewTag] = useState({ name: "", color: "#6366f1" });
  const [showAddCanned, setShowAddCanned] = useState(false);
  const [showAddTag, setShowAddTag] = useState(false);
  const [bans, setBans] = useState([]);
  const [newBan, setNewBan] = useState({ ip_address: "", reason: "" });
  const [showAddBan, setShowAddBan] = useState(false);
  const [invitations, setInvitations] = useState([]);
  const [showInviteForm, setShowInviteForm] = useState(false);
  const [newInvite, setNewInvite] = useState({ name: "", email: "", site_ids: [] });
  const [copiedToken, setCopiedToken] = useState(null);
  const [editingUser, setEditingUser] = useState(null);
  // Branding
  const [brandings, setBrandings] = useState([]);
  const [editBranding, setEditBranding] = useState(null); // currently editing
  const [brandingSaving, setBrandingSaving] = useState(false);
  const [showAddBranding, setShowAddBranding] = useState(false);
  const [newBrandingHostname, setNewBrandingHostname] = useState("");

  useEffect(() => {
    axios.get(`${API}/sites`).then(({ data }) => {
      setSites(data);
      if (data.length > 0) setSelectedSite(data[0]);
    });
    if (user?.role === "admin") {
      axios.get(`${API}/auth/users`).then(({ data }) => setTeamUsers(data)).catch(() => {});
    }
    axios.get(`${API}/bans`).then(({ data }) => setBans(data)).catch(() => {});
    if (user?.role === "admin") {
      axios.get(`${API}/auth/invitations`).then(({ data }) => setInvitations(data)).catch(() => {});
      axios.get(`${API}/branding/all`).then(({ data }) => {
        setBrandings(data);
        const def = data.find(b => b.hostname === "default") || { hostname: "default", ...globalBranding };
        setEditBranding({ ...def });
      }).catch(() => {});
    }
  }, []);

  useEffect(() => {
    if (!selectedSite) return;
    Promise.all([
      axios.get(`${API}/canned-responses?site_id=${selectedSite.id}`),
      axios.get(`${API}/tags?site_id=${selectedSite.id}`)
    ]).then(([cannedRes, tagsRes]) => {
      setCannedResponses(cannedRes.data);
      setTags(tagsRes.data);
    });
  }, [selectedSite]);

  const saveProfile = async () => {
    setSaving(true);
    try {
      const payload = { name: profile.name };
      if (profile.password) payload.password = profile.password;
      await axios.put(`${API}/auth/me`, payload);
    } catch {}
    setSaving(false);
  };

  const addCanned = async (e) => {
    e.preventDefault();
    if (!selectedSite) return;
    const { data } = await axios.post(`${API}/canned-responses`, { site_id: selectedSite.id, ...newCanned });
    setCannedResponses((prev) => [...prev, data]);
    setNewCanned({ title: "", content: "", shortcut: "" });
    setShowAddCanned(false);
  };

  const deleteCanned = async (id) => {
    await axios.delete(`${API}/canned-responses/${id}`);
    setCannedResponses((prev) => prev.filter((c) => c.id !== id));
  };

  const addTag = async (e) => {
    e.preventDefault();
    if (!selectedSite) return;
    const { data } = await axios.post(`${API}/tags`, { site_id: selectedSite.id, ...newTag });
    setTags((prev) => [...prev, data]);
    setNewTag({ name: "", color: "#6366f1" });
    setShowAddTag(false);
  };

  const deleteTag = async (id) => {
    await axios.delete(`${API}/tags/${id}`);
    setTags((prev) => prev.filter((t) => t.id !== id));
  };

  const updateUserRole = async (userId, role, isActive) => {
    await axios.put(`${API}/auth/users/${userId}/role`, { role, is_active: isActive });
    setTeamUsers((prev) => prev.map((u) => u.id === userId ? { ...u, role, is_active: isActive } : u));
  };

  const saveEditUser = async (e) => {
    e.preventDefault();
    try {
      const { data } = await axios.put(`${API}/auth/users/${editingUser.id}/role`, {
        name: editingUser.name,
        email: editingUser.email,
        role: editingUser.role,
        sso_only: editingUser.sso_only === true,
      });
      // Site erişimlerini de güncelle
      await axios.put(`${API}/auth/users/${editingUser.id}/sites`, { site_ids: editingUser.site_ids });
      setTeamUsers((prev) => prev.map((u) => u.id === data.id ? data : u));
      setEditingUser(null);
    } catch (err) {
      alert(err?.response?.data?.detail || "Hata oluştu");
    }
  };

  const deleteUser = async (userId) => {
    if (!window.confirm("Bu kullanıcıyı silmek istediğinize emin misiniz?")) return;
    await axios.delete(`${API}/auth/users/${userId}`);
    setTeamUsers((prev) => prev.filter((u) => u.id !== userId));
  };

  const addBan = async (e) => {
    e.preventDefault();
    try {
      const { data } = await axios.post(`${API}/bans`, newBan);
      setBans((prev) => [data, ...prev]);
      setNewBan({ ip_address: "", reason: "" });
      setShowAddBan(false);
    } catch (err) {
      alert(err?.response?.data?.detail || "Hata oluştu");
    }
  };

  const removeBan = async (id) => {
    await axios.delete(`${API}/bans/${id}`);
    setBans((prev) => prev.map((b) => b.id === id ? { ...b, is_active: false } : b));
  };

  const createInvite = async (e) => {
    e.preventDefault();
    try {
      const { data } = await axios.post(`${API}/auth/invite`, newInvite);
      setInvitations((prev) => [data, ...prev]);
      setNewInvite({ name: "", email: "", site_ids: [] });
      setShowInviteForm(false);
    } catch (err) {
      alert(err?.response?.data?.detail || "Hata oluştu");
    }
  };

  const deleteInvite = async (id) => {
    await axios.delete(`${API}/auth/invitations/${id}`);
    setInvitations((prev) => prev.filter(i => i.id !== id));
  };

  const copyInviteLink = (token) => {
    const link = `${FRONTEND_URL}/invite/${token}`;
    navigator.clipboard.writeText(link);
    setCopiedToken(token);
    setTimeout(() => setCopiedToken(null), 2500);
  };

  const toggleInviteSite = (siteId) => {
    setNewInvite(prev => ({
      ...prev,
      site_ids: prev.site_ids.includes(siteId)
        ? prev.site_ids.filter(id => id !== siteId)
        : [...prev.site_ids, siteId]
    }));
  };

  return (
    <Layout>
      <div className="p-4 md:p-8 max-w-4xl mx-auto">
        <div className="mb-8 animate-fade-in-up">
          <h1 className="font-heading text-3xl font-bold text-slate-900">{t("settings.title")}</h1>
          <p className="text-slate-500 mt-1">Hesap ve sistem ayarları</p>
        </div>

        <div className="flex gap-6">
          {/* Sidebar */}
          <div className="w-48 flex-shrink-0">
            <div className="space-y-1">
              {TABS.map(({ key, icon: Icon, label_key }) => (
                <button
                  key={key}
                  onClick={() => setTab(key)}
                  className={`w-full flex items-center gap-2.5 px-4 py-2.5 rounded-xl text-sm font-medium transition-colors ${
                    tab === key ? "bg-blue-50 text-blue-700" : "text-slate-600 hover:bg-slate-100"
                  }`}
                  data-testid={`settings-tab-${key}`}
                >
                  <Icon size={16} /> {t(label_key)}
                </button>
              ))}
            </div>
          </div>

          {/* Content */}
          <div className="flex-1 min-w-0">
            {/* Site selector for canned/tags */}
            {(tab === "canned" || tab === "tags") && sites.length > 1 && (
              <div className="mb-4">
                <select
                  value={selectedSite?.id || ""}
                  onChange={(e) => setSelectedSite(sites.find((s) => s.id === parseInt(e.target.value)))}
                  className="h-10 px-3 rounded-xl border border-slate-200 text-sm focus:outline-none focus:border-blue-400 bg-white"
                  data-testid="settings-site-select"
                >
                  {sites.map((s) => <option key={s.id} value={s.id}>{s.name}</option>)}
                </select>
              </div>
            )}

            {/* Profile Tab */}
            {tab === "profile" && (
              <div className="bg-white rounded-2xl border border-slate-100 p-6 animate-fade-in-up">
                <h2 className="font-semibold text-slate-900 mb-5">{t("settings.profile")}</h2>
                <div className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-1.5">{t("settings.name")}</label>
                    <input
                      value={profile.name}
                      onChange={(e) => setProfile({ ...profile, name: e.target.value })}
                      className="w-full h-11 rounded-xl border border-slate-200 px-4 text-sm focus:outline-none focus:border-blue-400"
                      data-testid="profile-name-input"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-1.5">{t("settings.email")}</label>
                    <input
                      value={profile.email}
                      disabled
                      className="w-full h-11 rounded-xl border border-slate-100 px-4 text-sm bg-slate-50 text-slate-500 cursor-not-allowed"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-1.5">{t("settings.password")}</label>
                    <input
                      type="password"
                      value={profile.password}
                      onChange={(e) => setProfile({ ...profile, password: e.target.value })}
                      className="w-full h-11 rounded-xl border border-slate-200 px-4 text-sm focus:outline-none focus:border-blue-400"
                      placeholder="••••••••"
                      data-testid="profile-password-input"
                    />
                  </div>
                  <button
                    onClick={saveProfile}
                    disabled={saving}
                    className="px-6 py-2.5 bg-blue-600 text-white rounded-xl text-sm font-semibold hover:bg-blue-700 transition-colors disabled:opacity-60 shadow-lg shadow-blue-500/25"
                    data-testid="save-profile-btn"
                  >
                    {saving ? t("common.loading") : t("common.save")}
                  </button>
                </div>
              </div>
            )}

            {/* Canned Responses */}
            {tab === "canned" && (
              <div className="bg-white rounded-2xl border border-slate-100 p-6 animate-fade-in-up">
                <div className="flex items-center justify-between mb-5">
                  <h2 className="font-semibold text-slate-900">{t("settings.canned")}</h2>
                  <button
                    onClick={() => setShowAddCanned(true)}
                    className="flex items-center gap-1.5 px-3 py-2 bg-blue-50 text-blue-700 text-xs font-medium rounded-lg hover:bg-blue-100 transition-colors"
                    data-testid="add-canned-btn"
                  >
                    <Plus size={13} /> {t("settings.add_canned")}
                  </button>
                </div>

                {showAddCanned && (
                  <form onSubmit={addCanned} className="mb-4 p-4 bg-blue-50 rounded-xl animate-fade-in-up">
                    <div className="space-y-3">
                      <input
                        value={newCanned.title}
                        onChange={(e) => setNewCanned({ ...newCanned, title: e.target.value })}
                        className="w-full h-10 rounded-lg border border-slate-200 px-3 text-sm focus:outline-none focus:border-blue-400"
                        placeholder={t("settings.canned_title")}
                        required data-testid="canned-title-input"
                      />
                      <textarea
                        value={newCanned.content}
                        onChange={(e) => setNewCanned({ ...newCanned, content: e.target.value })}
                        className="w-full rounded-lg border border-slate-200 px-3 py-2 text-sm focus:outline-none focus:border-blue-400 resize-none"
                        placeholder={t("settings.canned_content")}
                        rows={3} required data-testid="canned-content-input"
                      />
                      <input
                        value={newCanned.shortcut}
                        onChange={(e) => setNewCanned({ ...newCanned, shortcut: e.target.value })}
                        className="w-full h-10 rounded-lg border border-slate-200 px-3 text-sm focus:outline-none focus:border-blue-400"
                        placeholder={`${t("settings.shortcut")} (opsiyonel, ör: /merhaba)`}
                        data-testid="canned-shortcut-input"
                      />
                      <div className="flex gap-2">
                        <button type="submit" className="px-4 py-2 bg-blue-600 text-white rounded-lg text-sm font-medium hover:bg-blue-700 transition-colors" data-testid="save-canned-btn">{t("common.save")}</button>
                        <button type="button" onClick={() => setShowAddCanned(false)} className="px-4 py-2 bg-white border border-slate-200 text-slate-700 rounded-lg text-sm font-medium">{t("common.cancel")}</button>
                      </div>
                    </div>
                  </form>
                )}

                <div className="space-y-3">
                  {cannedResponses.map((cr) => (
                    <div key={cr.id} className="flex items-start gap-3 p-4 bg-slate-50 rounded-xl group" data-testid={`canned-item-${cr.id}`}>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2">
                          <span className="font-medium text-sm text-slate-900">{cr.title}</span>
                          {cr.shortcut && <span className="text-xs bg-slate-200 text-slate-600 px-1.5 py-0.5 rounded font-mono">{cr.shortcut}</span>}
                        </div>
                        <p className="text-xs text-slate-500 mt-0.5 truncate">{cr.content}</p>
                      </div>
                      <button onClick={() => deleteCanned(cr.id)} className="opacity-0 group-hover:opacity-100 transition-opacity text-red-400 hover:text-red-600" data-testid={`delete-canned-${cr.id}`}>
                        <Trash2 size={15} />
                      </button>
                    </div>
                  ))}
                  {cannedResponses.length === 0 && <p className="text-center text-slate-400 text-sm py-8">{t("common.no_data")}</p>}
                </div>
              </div>
            )}

            {/* Tags */}
            {tab === "tags" && (
              <div className="bg-white rounded-2xl border border-slate-100 p-6 animate-fade-in-up">
                <div className="flex items-center justify-between mb-5">
                  <h2 className="font-semibold text-slate-900">{t("settings.tags")}</h2>
                  <button
                    onClick={() => setShowAddTag(true)}
                    className="flex items-center gap-1.5 px-3 py-2 bg-blue-50 text-blue-700 text-xs font-medium rounded-lg hover:bg-blue-100 transition-colors"
                    data-testid="add-tag-btn"
                  >
                    <Plus size={13} /> {t("settings.add_tag")}
                  </button>
                </div>

                {showAddTag && (
                  <form onSubmit={addTag} className="mb-4 p-4 bg-blue-50 rounded-xl flex gap-3 items-end animate-fade-in-up">
                    <div className="flex-1">
                      <label className="block text-xs text-slate-600 mb-1">{t("settings.tag_name")}</label>
                      <input
                        value={newTag.name}
                        onChange={(e) => setNewTag({ ...newTag, name: e.target.value })}
                        className="w-full h-10 rounded-lg border border-slate-200 px-3 text-sm focus:outline-none focus:border-blue-400"
                        required data-testid="tag-name-input"
                      />
                    </div>
                    <div>
                      <label className="block text-xs text-slate-600 mb-1">{t("settings.tag_color")}</label>
                      <input type="color" value={newTag.color} onChange={(e) => setNewTag({ ...newTag, color: e.target.value })} className="w-10 h-10 rounded-lg border border-slate-200 cursor-pointer" data-testid="tag-color-input" />
                    </div>
                    <button type="submit" className="h-10 px-4 bg-blue-600 text-white rounded-lg text-sm font-medium hover:bg-blue-700 transition-colors" data-testid="save-tag-btn">{t("common.save")}</button>
                    <button type="button" onClick={() => setShowAddTag(false)} className="h-10 px-3 bg-white border border-slate-200 text-slate-700 rounded-lg text-sm">{t("common.cancel")}</button>
                  </form>
                )}

                <div className="flex flex-wrap gap-3">
                  {tags.map((tag) => (
                    <div key={tag.id} className="flex items-center gap-2 px-3 py-1.5 rounded-full text-white text-sm font-medium group" style={{ background: tag.color }} data-testid={`tag-chip-${tag.id}`}>
                      {tag.name}
                      <button onClick={() => deleteTag(tag.id)} className="opacity-0 group-hover:opacity-100 transition-opacity hover:scale-110" data-testid={`delete-tag-${tag.id}`}>
                        <X size={12} />
                      </button>
                    </div>
                  ))}
                  {tags.length === 0 && <p className="text-slate-400 text-sm">{t("common.no_data")}</p>}
                </div>
              </div>
            )}

            {/* Team */}
            {tab === "team" && (
              <div className="space-y-5 animate-fade-in-up">
                {/* Existing team members */}
                <div className="bg-white rounded-2xl border border-slate-100 p-6">
                  <h2 className="font-semibold text-slate-900 mb-4">Ekip Üyeleri</h2>
                  {user?.role !== "admin" ? (
                    <p className="text-slate-500 text-sm">Bu bölüm sadece adminlere görünür.</p>
                  ) : (
                    <div className="space-y-3">
                      {teamUsers.map((u) => (
                        <div key={u.id} className="border border-slate-100 rounded-xl overflow-hidden" data-testid={`team-user-${u.id}`}>
                          {/* Edit mode */}
                          {editingUser?.id === u.id ? (
                            <form onSubmit={saveEditUser} className="p-4 bg-blue-50 space-y-3">
                              <div className="grid grid-cols-2 gap-3">
                                <div>
                                  <label className="text-xs text-slate-500 mb-1 block">Ad Soyad</label>
                                  <input
                                    value={editingUser.name}
                                    onChange={(e) => setEditingUser({ ...editingUser, name: e.target.value })}
                                    className="w-full h-9 rounded-lg border border-slate-200 px-3 text-sm focus:outline-none focus:border-blue-400"
                                    required
                                  />
                                </div>
                                <div>
                                  <label className="text-xs text-slate-500 mb-1 block">E-posta</label>
                                  <input
                                    type="email"
                                    value={editingUser.email}
                                    onChange={(e) => setEditingUser({ ...editingUser, email: e.target.value })}
                                    className="w-full h-9 rounded-lg border border-slate-200 px-3 text-sm focus:outline-none focus:border-blue-400"
                                    required
                                  />
                                </div>
                              </div>

                              {/* Rol seçimi */}
                              <div>
                                <label className="text-xs text-slate-500 mb-1.5 block">Rol</label>
                                <select
                                  value={editingUser.role}
                                  onChange={(e) => setEditingUser({ ...editingUser, role: e.target.value })}
                                  className="h-9 px-2 rounded-lg border border-slate-200 text-sm focus:outline-none bg-white"
                                  data-testid="edit-user-role-select"
                                >
                                  <option value="admin">Admin</option>
                                  <option value="agent">Agent</option>
                                </select>
                              </div>

                              {/* SSO zorunlu — işaretliyse şifreyle giremez, sadece Netlen ID */}
                              <div>
                                <label className="flex items-center gap-2.5 cursor-pointer select-none">
                                  <input
                                    type="checkbox"
                                    checked={editingUser.sso_only === true}
                                    onChange={(e) => setEditingUser({ ...editingUser, sso_only: e.target.checked })}
                                    className="h-4 w-4 rounded border-slate-300 text-blue-600 focus:ring-blue-400"
                                    data-testid="edit-user-sso-only"
                                  />
                                  <span className="text-sm text-slate-700">Sadece Netlen ID ile giriş (şifreyle giremez)</span>
                                </label>
                              </div>

                              {/* Site erişim yönetimi — sadece agent için anlamlı */}
                              {editingUser.role === "agent" && sites.length > 0 && (
                                <div>
                                  <label className="text-xs text-slate-500 mb-1.5 block">Site Erişimleri</label>
                                  <div className="flex flex-wrap gap-2">
                                    {sites.map((s) => {
                                      const selected = (editingUser.site_ids || []).includes(s.id);
                                      return (
                                        <button
                                          type="button"
                                          key={s.id}
                                          onClick={() => setEditingUser(prev => ({
                                            ...prev,
                                            site_ids: selected
                                              ? prev.site_ids.filter(id => id !== s.id)
                                              : [...(prev.site_ids || []), s.id]
                                          }))}
                                          className={`px-3 py-1.5 rounded-lg text-xs font-medium border transition-colors ${
                                            selected
                                              ? "bg-blue-600 text-white border-blue-600"
                                              : "bg-white text-slate-600 border-slate-200 hover:border-blue-300"
                                          }`}
                                          data-testid={`edit-site-toggle-${s.id}`}
                                        >
                                          {s.name}
                                        </button>
                                      );
                                    })}
                                  </div>
                                  {(editingUser.site_ids || []).length === 0 && (
                                    <p className="text-xs text-amber-600 mt-1">Hiç site seçilmezse kullanıcı hiçbir konuşmayı göremez</p>
                                  )}
                                </div>
                              )}

                              <div className="flex items-center gap-2 pt-1">
                                <button type="submit" className="flex items-center gap-1.5 px-3 py-2 bg-blue-600 text-white rounded-lg text-xs font-medium hover:bg-blue-700 transition-colors" data-testid="save-edit-user-btn">
                                  <Save size={12} /> Kaydet
                                </button>
                                <button type="button" onClick={() => setEditingUser(null)} className="px-3 py-2 bg-white border border-slate-200 text-slate-600 rounded-lg text-xs hover:bg-slate-50 transition-colors">
                                  Vazgeç
                                </button>
                              </div>
                            </form>
                          ) : (
                            /* View mode */
                            <div className="flex items-center gap-3 p-4 bg-white">
                              <div className={`w-9 h-9 rounded-full flex items-center justify-center text-sm font-bold flex-shrink-0 ${u.is_active ? "bg-blue-100 text-blue-700" : "bg-slate-100 text-slate-400"}`}>
                                {u.name?.[0]?.toUpperCase()}
                              </div>
                              <div className="flex-1 min-w-0">
                                <div className="flex items-center gap-2">
                                  <span className="font-medium text-sm text-slate-900">{u.name}</span>
                                  <span className={`text-xs px-1.5 py-0.5 rounded-full font-medium ${u.role === "admin" ? "bg-purple-100 text-purple-700" : "bg-slate-100 text-slate-500"}`}>
                                    {u.role === "admin" ? "Admin" : "Agent"}
                                  </span>
                                  {!u.is_active && <span className="text-xs bg-red-100 text-red-600 px-1.5 py-0.5 rounded-full">Pasif</span>}
                                </div>
                                <div className="text-xs text-slate-400 mt-0.5">{u.email}</div>
                              </div>
                              <div className="flex items-center gap-1.5 flex-shrink-0">
                                {/* Düzenle */}
                                <button
                                  onClick={async () => {
                                    let site_ids = [];
                                    try {
                                      const { data: sd } = await axios.get(`${API}/auth/users/${u.id}/sites`);
                                      site_ids = sd.site_ids || [];
                                    } catch {}
                                    setEditingUser({ id: u.id, name: u.name, email: u.email, role: u.role, sso_only: u.sso_only === true, site_ids });
                                  }}
                                  className="p-2 rounded-lg bg-slate-50 text-slate-500 hover:bg-blue-50 hover:text-blue-600 transition-colors"
                                  title="Düzenle"
                                  data-testid={`edit-user-${u.id}`}
                                >
                                  <Edit2 size={13} />
                                </button>
                                {/* Aktif / Pasif */}
                                <button
                                  onClick={() => updateUserRole(u.id, u.role, !u.is_active)}
                                  disabled={u.id === user.id}
                                  className={`text-xs px-2.5 py-1.5 rounded-lg font-medium transition-colors disabled:opacity-40 whitespace-nowrap ${
                                    u.is_active
                                      ? "bg-amber-50 text-amber-700 hover:bg-amber-100"
                                      : "bg-emerald-50 text-emerald-700 hover:bg-emerald-100"
                                  }`}
                                  data-testid={`user-toggle-${u.id}`}
                                >
                                  {u.is_active ? "Devre Dışı Bırak" : "Aktif Et"}
                                </button>
                                {/* Sil */}
                                <button
                                  onClick={() => deleteUser(u.id)}
                                  disabled={u.id === user.id}
                                  className="p-2 rounded-lg bg-slate-50 text-slate-400 hover:bg-red-50 hover:text-red-600 transition-colors disabled:opacity-40"
                                  title="Sil"
                                  data-testid={`delete-user-${u.id}`}
                                >
                                  <Trash2 size={13} />
                                </button>
                              </div>
                            </div>
                          )}
                        </div>
                      ))}
                    </div>
                  )}
                </div>

                {/* Invite system - admin only */}
                {user?.role === "admin" && (
                  <div className="bg-white rounded-2xl border border-slate-100 p-6">
                    <div className="flex items-center justify-between mb-4">
                      <div>
                        <h2 className="font-semibold text-slate-900">Davetler</h2>
                        <p className="text-xs text-slate-400 mt-0.5">Yeni üye davet edin, link paylaşın</p>
                      </div>
                      <button
                        onClick={() => setShowInviteForm(true)}
                        className="flex items-center gap-1.5 px-3 py-2 bg-blue-50 text-blue-700 text-xs font-medium rounded-lg hover:bg-blue-100 transition-colors"
                        data-testid="invite-member-btn"
                      >
                        <Plus size={13} /> Davet Gönder
                      </button>
                    </div>

                    {/* Invite form */}
                    {showInviteForm && (
                      <form onSubmit={createInvite} className="mb-5 p-4 bg-blue-50 rounded-xl space-y-3 animate-fade-in-up">
                        <div className="grid grid-cols-2 gap-3">
                          <div>
                            <label className="block text-xs font-medium text-slate-700 mb-1">Ad Soyad</label>
                            <input
                              value={newInvite.name}
                              onChange={(e) => setNewInvite({ ...newInvite, name: e.target.value })}
                              placeholder="Ahmet Yılmaz"
                              className="w-full h-10 rounded-lg border border-slate-200 px-3 text-sm focus:outline-none focus:border-blue-400"
                              required data-testid="invite-name-input"
                            />
                          </div>
                          <div>
                            <label className="block text-xs font-medium text-slate-700 mb-1">E-posta</label>
                            <input
                              type="email"
                              value={newInvite.email}
                              onChange={(e) => setNewInvite({ ...newInvite, email: e.target.value })}
                              placeholder="ahmet@firma.com"
                              className="w-full h-10 rounded-lg border border-slate-200 px-3 text-sm focus:outline-none focus:border-blue-400"
                              required data-testid="invite-email-input"
                            />
                          </div>
                        </div>

                        {/* Site selection */}
                        <div>
                          <label className="block text-xs font-medium text-slate-700 mb-2">Site Yetkileri (birden fazla seçilebilir)</label>
                          <div className="flex flex-wrap gap-2">
                            {sites.map((s) => (
                              <button
                                type="button"
                                key={s.id}
                                onClick={() => toggleInviteSite(s.id)}
                                className={`px-3 py-1.5 rounded-lg text-xs font-medium border transition-colors ${
                                  newInvite.site_ids.includes(s.id)
                                    ? "bg-blue-600 text-white border-blue-600"
                                    : "bg-white text-slate-600 border-slate-200 hover:border-blue-300"
                                }`}
                                data-testid={`site-toggle-${s.id}`}
                              >
                                {s.name}
                              </button>
                            ))}
                          </div>
                          {newInvite.site_ids.length === 0 && (
                            <p className="text-xs text-amber-600 mt-1">En az bir site seçin</p>
                          )}
                        </div>

                        <div className="flex gap-2">
                          <button
                            type="submit"
                            disabled={newInvite.site_ids.length === 0}
                            className="px-4 py-2 bg-blue-600 text-white rounded-lg text-sm font-medium hover:bg-blue-700 transition-colors disabled:opacity-50"
                            data-testid="create-invite-btn"
                          >
                            Davet Oluştur
                          </button>
                          <button type="button" onClick={() => setShowInviteForm(false)} className="px-4 py-2 bg-white border border-slate-200 text-slate-700 rounded-lg text-sm">Vazgeç</button>
                        </div>
                      </form>
                    )}

                    {/* Invitations list */}
                    {invitations.length === 0 ? (
                      <div className="text-center py-8">
                        <Link size={28} className="text-slate-200 mx-auto mb-2" />
                        <p className="text-slate-400 text-sm">Henüz davet gönderilmemiş</p>
                      </div>
                    ) : (
                      <div className="space-y-2">
                        {invitations.map((inv) => (
                          <div key={inv.id} className={`flex items-center gap-3 p-3 rounded-xl border ${inv.is_used ? "border-slate-100 bg-slate-50 opacity-60" : "border-blue-100 bg-blue-50"}`} data-testid={`invite-row-${inv.id}`}>
                            <div className="flex-1 min-w-0">
                              <div className="flex items-center gap-2">
                                <span className="font-medium text-sm text-slate-800">{inv.name}</span>
                                {inv.is_used ? (
                                  <span className="text-xs bg-emerald-100 text-emerald-700 px-2 py-0.5 rounded-full">Kabul edildi</span>
                                ) : (
                                  <span className="text-xs bg-amber-100 text-amber-700 px-2 py-0.5 rounded-full">Bekliyor</span>
                                )}
                              </div>
                              <div className="text-xs text-slate-500 mt-0.5">{inv.email}</div>
                            </div>
                            {!inv.is_used && (
                              <button
                                onClick={() => copyInviteLink(inv.token)}
                                className="flex items-center gap-1.5 px-3 py-1.5 bg-white border border-slate-200 rounded-lg text-xs text-slate-600 hover:bg-slate-50 transition-colors flex-shrink-0"
                                data-testid={`copy-invite-${inv.id}`}
                              >
                                {copiedToken === inv.token ? <><Check size={12} className="text-emerald-500" /> Kopyalandı</> : <><Copy size={12} /> Link Kopyala</>}
                              </button>
                            )}
                            {!inv.is_used && (
                              <button onClick={() => deleteInvite(inv.id)} className="text-slate-400 hover:text-red-500 transition-colors" data-testid={`delete-invite-${inv.id}`}>
                                <X size={15} />
                              </button>
                            )}
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                )}
              </div>
            )}

            {/* IP Bans */}
            {tab === "bans" && (
              <div className="bg-white rounded-2xl border border-slate-100 p-6 animate-fade-in-up">
                <div className="flex items-center justify-between mb-5">
                  <div>
                    <h2 className="font-semibold text-slate-900">Yasaklı IP Adresleri</h2>
                    <p className="text-xs text-slate-400 mt-0.5">Yasaklanan IP'ler widget'ı göremez</p>
                  </div>
                  <button
                    onClick={() => setShowAddBan(true)}
                    className="flex items-center gap-1.5 px-3 py-2 bg-red-50 text-red-700 text-xs font-medium rounded-lg hover:bg-red-100 transition-colors"
                    data-testid="add-ban-btn"
                  >
                    <Plus size={13} /> IP Ekle
                  </button>
                </div>

                {showAddBan && (
                  <form onSubmit={addBan} className="mb-5 p-4 bg-red-50 rounded-xl space-y-3 animate-fade-in-up">
                    <div className="grid grid-cols-2 gap-3">
                      <div>
                        <label className="block text-xs text-slate-600 mb-1">IP Adresi</label>
                        <input
                          value={newBan.ip_address}
                          onChange={(e) => setNewBan({ ...newBan, ip_address: e.target.value })}
                          placeholder="192.168.1.1"
                          className="w-full h-10 rounded-lg border border-slate-200 px-3 text-sm focus:outline-none focus:border-red-400"
                          required data-testid="ban-ip-input"
                        />
                      </div>
                      <div>
                        <label className="block text-xs text-slate-600 mb-1">Sebep</label>
                        <input
                          value={newBan.reason}
                          onChange={(e) => setNewBan({ ...newBan, reason: e.target.value })}
                          placeholder="Spam, kötüye kullanım..."
                          className="w-full h-10 rounded-lg border border-slate-200 px-3 text-sm focus:outline-none focus:border-red-400"
                          required data-testid="ban-reason-input-settings"
                        />
                      </div>
                    </div>
                    <div className="flex gap-2">
                      <button type="submit" className="px-4 py-2 bg-red-600 text-white rounded-lg text-sm font-medium hover:bg-red-700 transition-colors" data-testid="save-ban-btn">Yasakla</button>
                      <button type="button" onClick={() => setShowAddBan(false)} className="px-4 py-2 bg-white border border-slate-200 text-slate-700 rounded-lg text-sm">Vazgeç</button>
                    </div>
                  </form>
                )}

                {bans.length === 0 ? (
                  <div className="text-center py-10">
                    <ShieldCheck size={32} className="text-slate-200 mx-auto mb-3" />
                    <p className="text-slate-400 text-sm">Yasaklı IP adresi yok</p>
                  </div>
                ) : (
                  <div className="space-y-2">
                    {bans.map((ban) => (
                      <div key={ban.id} className={`flex items-center gap-3 p-3 rounded-xl border ${ban.is_active ? "border-red-100 bg-red-50" : "border-slate-100 bg-slate-50 opacity-50"}`} data-testid={`ban-row-${ban.id}`}>
                        <ShieldOff size={16} className={ban.is_active ? "text-red-500 flex-shrink-0" : "text-slate-400 flex-shrink-0"} />
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2">
                            <span className="font-mono text-sm font-semibold text-slate-800">{ban.ip_address}</span>
                            {!ban.is_active && <span className="text-xs bg-slate-200 text-slate-500 px-2 py-0.5 rounded-full">Kaldırıldı</span>}
                          </div>
                          <div className="text-xs text-slate-500 mt-0.5 flex items-center gap-3">
                            <span>{ban.reason}</span>
                            <span className="text-slate-300">•</span>
                            <span>Yasaklayan: {ban.banned_by}</span>
                            <span className="text-slate-300">•</span>
                            <span>{ban.created_at ? parseUTC(ban.created_at).toLocaleDateString("tr-TR") : ""}</span>
                          </div>
                        </div>
                        {ban.is_active && (
                          <button
                            onClick={() => removeBan(ban.id)}
                            className="flex items-center gap-1 px-2.5 py-1.5 bg-white border border-slate-200 text-slate-600 text-xs rounded-lg hover:bg-slate-50 transition-colors flex-shrink-0"
                            data-testid={`unban-btn-${ban.id}`}
                          >
                            <ShieldCheck size={12} /> Kaldır
                          </button>
                        )}
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}

            {/* ── Marka Yönetimi ── */}
            {tab === "branding" && user?.role === "admin" && (
              <div className="space-y-6">
                <div className="flex items-center justify-between">
                  <div>
                    <h2 className="font-semibold text-slate-900">Marka Yönetimi</h2>
                    <p className="text-xs text-slate-500 mt-0.5">Her domain için ayrı logo, isim ve footer ayarı. <code className="bg-slate-100 px-1 rounded">default</code> tüm domainler için geçerlidir.</p>
                  </div>
                  <button
                    onClick={() => setShowAddBranding(true)}
                    className="flex items-center gap-1.5 px-3 py-2 bg-blue-600 text-white text-sm rounded-xl hover:bg-blue-700 transition-colors"
                    data-testid="add-branding-btn"
                  >
                    <Plus size={14} /> Yeni Domain
                  </button>
                </div>

                {/* Yeni domain formu */}
                {showAddBranding && (
                  <div className="bg-blue-50 border border-blue-100 rounded-xl p-4 flex items-center gap-3">
                    <input
                      value={newBrandingHostname}
                      onChange={e => setNewBrandingHostname(e.target.value)}
                      placeholder="livechat.musteridomain.com"
                      className="flex-1 h-10 rounded-lg border border-slate-200 px-3 text-sm focus:outline-none focus:border-blue-400"
                      data-testid="new-branding-hostname-input"
                    />
                    <button
                      onClick={async () => {
                        if (!newBrandingHostname.trim()) return;
                        const { data } = await axios.post(`${API}/branding`, { hostname: newBrandingHostname.trim() });
                        setBrandings(prev => [...prev, data]);
                        setEditBranding({ ...data });
                        setNewBrandingHostname("");
                        setShowAddBranding(false);
                      }}
                      className="px-4 py-2 bg-blue-600 text-white text-sm rounded-xl hover:bg-blue-700"
                    >Ekle</button>
                    <button onClick={() => setShowAddBranding(false)} className="text-slate-400 hover:text-slate-600"><X size={16} /></button>
                  </div>
                )}

                {/* Domain listesi */}
                <div className="flex gap-2 flex-wrap">
                  {brandings.map(b => (
                    <button
                      key={b.id || b.hostname}
                      onClick={() => setEditBranding({ ...b })}
                      className={`px-3 py-1.5 rounded-xl text-sm font-medium border transition-colors ${editBranding?.hostname === b.hostname ? "bg-slate-900 text-white border-slate-900" : "bg-white text-slate-700 border-slate-200 hover:border-slate-300"}`}
                      data-testid={`branding-tab-${b.hostname}`}
                    >
                      {b.hostname === "default" ? "Global (varsayılan)" : b.hostname}
                    </button>
                  ))}
                </div>

                {/* Düzenleme formu */}
                {editBranding && (
                  <div className="bg-white border border-slate-100 rounded-2xl p-6 space-y-4" data-testid="branding-edit-form">
                    <div className="flex items-center justify-between mb-2">
                      <h3 className="font-semibold text-slate-800 text-sm">
                        {editBranding.hostname === "default" ? "Global Marka Ayarları" : editBranding.hostname}
                      </h3>
                      {editBranding.hostname !== "default" && (
                        <button
                          onClick={async () => {
                            if (!window.confirm("Bu marka ayarını silmek istediğinizden emin misiniz?")) return;
                            await axios.delete(`${API}/branding/${editBranding.id}`);
                            setBrandings(prev => prev.filter(b => b.hostname !== editBranding.hostname));
                            const def = brandings.find(b => b.hostname === "default");
                            setEditBranding(def ? { ...def } : null);
                          }}
                          className="flex items-center gap-1 text-xs text-red-500 hover:text-red-700"
                        ><Trash2 size={13} /> Sil</button>
                      )}
                    </div>

                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                      <div>
                        <label className="block text-xs font-medium text-slate-600 mb-1.5">Marka Adı</label>
                        <input
                          value={editBranding.brand_name || ""}
                          onChange={e => setEditBranding({ ...editBranding, brand_name: e.target.value })}
                          className="w-full h-10 rounded-xl border border-slate-200 px-3 text-sm focus:outline-none focus:border-blue-400"
                          placeholder="Netlen Canlı Destek"
                          data-testid="branding-name-input"
                        />
                      </div>
                      <div>
                        <label className="block text-xs font-medium text-slate-600 mb-1.5">Footer Metni</label>
                        <input
                          value={editBranding.footer_text || ""}
                          onChange={e => setEditBranding({ ...editBranding, footer_text: e.target.value })}
                          className="w-full h-10 rounded-xl border border-slate-200 px-3 text-sm focus:outline-none focus:border-blue-400"
                          placeholder="© 2026 Şirket Adı"
                          data-testid="branding-footer-input"
                        />
                      </div>
                      <div>
                        <label className="block text-xs font-medium text-slate-600 mb-1.5">Logo URL</label>
                        <input
                          value={editBranding.logo_url || ""}
                          onChange={e => setEditBranding({ ...editBranding, logo_url: e.target.value })}
                          className="w-full h-10 rounded-xl border border-slate-200 px-3 text-sm focus:outline-none focus:border-blue-400"
                          placeholder="/netlen-icon.png veya https://..."
                          data-testid="branding-logo-input"
                        />
                      </div>
                      <div>
                        <label className="block text-xs font-medium text-slate-600 mb-1.5">Favicon URL</label>
                        <input
                          value={editBranding.favicon_url || ""}
                          onChange={e => setEditBranding({ ...editBranding, favicon_url: e.target.value })}
                          className="w-full h-10 rounded-xl border border-slate-200 px-3 text-sm focus:outline-none focus:border-blue-400"
                          placeholder="/favicon.png veya https://..."
                          data-testid="branding-favicon-input"
                        />
                      </div>
                      <div className="sm:col-span-2">
                        <label className="block text-xs font-medium text-slate-600 mb-1.5">Login Sayfa Alt Başlığı</label>
                        <input
                          value={editBranding.login_subtitle || ""}
                          onChange={e => setEditBranding({ ...editBranding, login_subtitle: e.target.value })}
                          className="w-full h-10 rounded-xl border border-slate-200 px-3 text-sm focus:outline-none focus:border-blue-400"
                          placeholder="AI destekli canlı destek sistemi"
                          data-testid="branding-subtitle-input"
                        />
                      </div>
                    </div>

                    {/* Önizleme */}
                    {editBranding.logo_url && (
                      <div className="flex items-center gap-3 p-3 bg-slate-900 rounded-xl">
                        <img src={editBranding.logo_url} alt="logo" className="w-8 h-8 rounded-lg object-cover" onError={e => e.target.style.display='none'} />
                        <span className="text-white text-sm font-semibold">{editBranding.brand_name}</span>
                        <span className="text-slate-500 text-xs ml-auto">{editBranding.footer_text}</span>
                      </div>
                    )}

                    <button
                      onClick={async () => {
                        setBrandingSaving(true);
                        try {
                          const { data } = await axios.post(`${API}/branding`, editBranding);
                          setBrandings(prev => prev.map(b => b.hostname === data.hostname ? data : b).concat(
                            prev.find(b => b.hostname === data.hostname) ? [] : [data]
                          ));
                          setEditBranding({ ...data });
                          alert("Kaydedildi! Değişiklikler sayfa yenilenince aktif olur.");
                        } finally {
                          setBrandingSaving(false);
                        }
                      }}
                      disabled={brandingSaving}
                      className="flex items-center gap-2 px-4 py-2.5 bg-slate-900 text-white text-sm font-medium rounded-xl hover:bg-slate-800 disabled:opacity-60 transition-colors"
                      data-testid="branding-save-btn"
                    >
                      <Save size={14} /> {brandingSaving ? "Kaydediliyor..." : "Kaydet"}
                    </button>
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      </div>
    </Layout>
  );
}
